import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';

import { useTranslation } from '@apple/lib/i18next';
import { BlockNavigation } from '@apple/ui/form/components/BlockNavigation';
import { ValidatedForm } from '@apple/ui/form/components/ValidatedForm/ValidatedForm';
import { StepLayout } from '@apple/ui/layouts/step';
import { LoadingOverlay } from '@apple/ui/loading-overlay';
import { useLogger } from '@apple/utils/logging/useLogger';

import { CategoriesView } from '@apple/features/store-events/components/categories/categories-view';
import { ConflictResolutions } from '@apple/features/store-events/components/conflicts/resolutions';
import { useArchiveStoreEvent } from '@apple/features/store-events/hooks/use-archive-store-event';
import { useSelectedCategories } from '@apple/features/store-events/hooks/use-selected-categories';
import {
	type StoreEventCategorySelectionUpdateRequest,
	storeEventCategorySelectionUpdateRequestSchema,
} from '@apple/features/store-events/models/categories';
import { storeEventQueries } from '@apple/features/store-events/queries';

export const Route = createFileRoute('/_authed/store-events/_steps/$storeEventId/categories')({
	component: RouteComponent,
});

function RouteComponent() {
	const { t } = useTranslation('store-event');
	const { storeEventId } = Route.useParams();
	const navigate = Route.useNavigate();
	const storeEventQuery = useQuery(storeEventQueries.getById(storeEventId));
	const selectedCategories = useSelectedCategories(storeEventId);
	const archiveMutation = useArchiveStoreEvent(storeEventId);
	const canUpdateCategories =
		(storeEventQuery.data?.canBeCompleted && !storeEventQuery.data.canBeSubmitted) ||
		storeEventQuery.data?.isOpen;

	useLogger({
		log: 'store-events',
		name: 'categories',
		props: [{ storeEventId, selectedCategories }],
	});

	return selectedCategories.query.isPending ? (
		<LoadingOverlay visible />
	) : (
		<ValidatedForm<StoreEventCategorySelectionUpdateRequest>
			name='store-event-categories'
			initialValues={storeEventCategorySelectionUpdateRequestSchema.parse({
				categorySelections: selectedCategories.query.data,
				conflictResolutions: [],
				comment: 'N/A',
			})}
			onValidSubmit={async values => {
				await selectedCategories.mutation.mutateAsync(values, {
					onSuccess: async data => {
						if (!data || data.length <= 0) {
							await navigate({
								to: '/store-events/$storeEventId/products',
								params: {
									storeEventId,
								},
							});
						}
					},
				});
			}}
		>
			{({ form, isSubmitting }) => (
				<StepLayout
					queryErrors={[selectedCategories.query.error]}
					mutationErrors={[selectedCategories.mutation.error, archiveMutation.error]}
					buttons={[
						{
							text: t('common:buttons.back'),
							hidden: !selectedCategories.hasConflicts,
							onClick: () => {
								form.setFieldValue('conflictResolutions', []);
								selectedCategories.clearConflicts();
							},
						},
						{
							text: t('common:buttons.back'),
							hidden: selectedCategories.hasConflicts,
							to: '/store-events/$storeEventId/details',
							params: {
								storeEventId,
							},
						},
						{
							text: t('labels.archive'),
							hidden: !storeEventQuery.data?.canBeArchived,
							onClick: () =>
								archiveMutation.mutate(undefined, {
									onSuccess: () => {
										navigate({
											to: '/store-events',
										});
									},
								}),
							color: 'red',
							variant: 'filled',
							disabled:
								form.isDirty() ||
								selectedCategories.mutation.isPending ||
								archiveMutation.isPending ||
								isSubmitting,
							loading: archiveMutation.isPending,
						},
						{
							text: t('common:pagination.nextShort'),
							hidden: !form.isDirty(),
							type: 'submit',
							color: 'blue',
							variant: 'filled',
							loading: selectedCategories.mutation.isPending || isSubmitting,
							disabled:
								selectedCategories.mutation.isPending ||
								isSubmitting ||
								form.values.categorySelections.length === 0,
						},
						{
							text: t('common:pagination.nextShort'),
							hidden: form.isDirty(),
							to: '/store-events/$storeEventId/products',
							params: {
								storeEventId,
							},
							color: 'blue',
							variant: 'filled',
							disabled: form.values.categorySelections.length === 0,
						},
					]}
				>
					<BlockNavigation
						condition={
							// Stop navigation when the form is dirty and not saved
							form.isDirty() &&
							!(
								selectedCategories.mutation.isPending ||
								selectedCategories.mutation.isSuccess ||
								isSubmitting
							)
						}
					/>
					{selectedCategories.hasConflicts ? (
						<ConflictResolutions
							conflicts={selectedCategories.conflicts}
							onConflictsResolved={resolutions =>
								form.setFieldValue('conflictResolutions', resolutions)
							}
						/>
					) : (
						<CategoriesView form={form} readOnly={!canUpdateCategories} />
					)}
				</StepLayout>
			)}
		</ValidatedForm>
	);
}
